import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ContactForm";
import { CocoonMark } from "@/components/CocoonMark";

export default function Faq() {
  const { t } = useTranslation();
  const items = t("faq.items", { returnObjects: true }) || [];
  const [open, setOpen] = useState(0);

  return (
    <div className="relative min-h-screen bg-background text-foreground starfield overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-8%", left: "50%", transform: "translateX(-50%)", width: "860px", height: "620px" }} />
      <div className="amber-glow" aria-hidden style={{ top: "64%", right: "-14%", left: "auto", width: "600px", height: "600px", opacity: 0.55 }} />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="faq-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/manifeste" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.manifeste")}</Link>
            <Link to="/tarifs" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.tarifs")}</Link>
            <Link to="/entrer" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-36 pb-24">
        <div className="text-center mb-14">
          <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">{t("faq.eyebrow")}</p>
          <h1 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-5">{t("faq.title")}</h1>
          <p className="text-foreground/60 leading-relaxed">{t("faq.subtitle")}</p>
        </div>

        <div className="space-y-3" data-testid="faq-list">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="glass rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display text-lg text-foreground/90">{it.q}</span>
                  <ChevronDown size={20} className={`text-primary flex-shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p className="px-6 pb-6 text-foreground/65 leading-relaxed" data-testid={`faq-answer-${i}`}>{it.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        <div className="mt-16">
          <ContactForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
